import { useEffect, useMemo, useState } from 'react'
import { Box, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import usersApi, { BadgeProgress } from '../../client/users-api'
import {
  readBadgesCache,
  writeBadgesCache,
  readBadgesSignature,
  writeBadgesSignature,
} from '../../context/badges.cache'
import { useAuth } from '../../context/AuthContext'
import { useMediaState } from '../../context/media-state-context'
import { RailCard } from './rail-styles'

// Own-profile badge case. Earned badges are trophies and compress to chips;
// only the badge closest to earning gets a full card, with the rest of the
// in-progress set behind a toggle. Every badge's description lives in a
// tooltip rather than on the surface. Owner-only - the public profile page
// keeps showing earned chips only. Token fetched explicitly so a mount-time
// race can never send an unauthenticated request (401s trigger the global
// session-expired redirect).

const iconByKey: Record<string, string> = {
  'binge-watcher': '📺',
  'marathon-legend': '🏆',
  'movie-buff': '🎬',
  cinephile: '🎞️',
  finisher: '🏁',
  curator: '❤️',
  conversationalist: '💬',
  og: '🌟',
}

const iconFor = (key: string) => iconByKey[key] ?? '🎖️'

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}))

const HeaderLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}))

const HeaderCount = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}))

const ChipRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}))

// Earned badge: accent-tinted pill, icon + label only.
const EarnedChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.25),
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'default',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.brand.main}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(139, 30, 63, 0.22)'
      : 'rgba(139, 30, 63, 0.07)',
}))

const Divider = styled(Box)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.divider,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}))

const ClosestLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.brand.main,
  marginBottom: theme.spacing(1),
}))

const BadgeName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.primary,
}))

const IconAndName = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'default',
}))

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.action.hover,
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
    backgroundColor: theme.palette.brand.main,
  },
}))

const ProgressCaption = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.75),
}))

// Remaining in-progress badges: one compact row each, name left, count right.
const CompactRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  cursor: 'default',
}))

const CompactName = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.primary,
  flexGrow: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const CompactCount = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  flexShrink: 0,
}))

const ToggleButton = styled('button')(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: theme.palette.brand.main,
  '&:hover': { textDecoration: 'underline' },
}))

const BadgesSection = () => {
  const { isAuthenticated, getAccessToken } = useAuth()
  const { mediaState } = useMediaState()
  // Seed from the session cache so switching to the profile tab renders the
  // badges instantly.
  const [badges, setBadges] = useState<BadgeProgress[]>(() => readBadgesCache() ?? [])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showAllInProgress, setShowAllInProgress] = useState(false)

  // Fingerprint of the badge inputs that live in the media-state provider
  // (episodes watched, movies watched, favorites). Comments are intentionally
  // NOT part of this - Conversationalist updates on the next full reload. When
  // this matches the cached signature, nothing that feeds a badge has changed,
  // so a profile visit skips the fetch entirely. Undefined stats (not loaded
  // yet) yields null, which never matches, so the first real load still fetches.
  const stats = mediaState?.stats
  const signature = stats
    ? `${stats.totalEpisodesWatched}|${stats.totalMoviesWatched}|${stats.totalFavorites ?? 0}`
    : null

  useEffect(() => {
    if (!isAuthenticated) {
      setBadges([])
      writeBadgesCache(null)
      return
    }
    // Wait until media-state has loaded so we have a real signature to compare.
    if (signature === null) return
    // Nothing that drives a badge changed since the cached badges were computed:
    // keep showing the cache, make no request.
    if (readBadgesCache() && readBadgesSignature() === signature) return

    let cancelled = false
    setIsRefreshing(true)
    ;(async () => {
      try {
        const token = await getAccessToken()
        if (!token || cancelled) return
        const res = await usersApi.getMyBadges(token)
        if (!cancelled) {
          setBadges(res.data)
          writeBadgesCache(res.data)
          writeBadgesSignature(signature)
        }
      } catch {
        // Keep whatever the cache already rendered; only clear on a real
        // signed-out state (handled above), never on a transient fetch error.
      } finally {
        if (!cancelled) setIsRefreshing(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, getAccessToken, signature])

  const earned = useMemo(() => badges.filter((b) => b.earned), [badges])

  // Closest to earning = highest completion ratio, so the card always shows the
  // badge the user is most likely to finish next.
  const inProgress = useMemo(
    () =>
      badges
        .filter((b) => !b.earned)
        .sort(
          (a, b) =>
            b.current / (b.threshold || 1) - a.current / (a.threshold || 1),
        ),
    [badges],
  )

  const [closest, ...restInProgress] = inProgress

  // Cached badges render immediately; only a cold load has nothing to show.
  if (badges.length === 0 && isRefreshing) {
    return (
      <RailCard>
        <HeaderRow>
          <HeaderLabel>Badges</HeaderLabel>
        </HeaderRow>
        <ChipRow>
          <Skeleton variant='rounded' width={132} height={30} />
          <Skeleton variant='rounded' width={104} height={30} />
        </ChipRow>
        <Divider />
        <Skeleton variant='text' width={128} height={18} />
        <Skeleton variant='rounded' height={6} sx={{ mt: 1 }} />
      </RailCard>
    )
  }

  // Never block the profile on this section; no data just hides it.
  if (badges.length === 0) return null

  return (
    <RailCard>
      <HeaderRow>
        <HeaderLabel>Badges</HeaderLabel>
        <HeaderCount>
          {earned.length} / {badges.length}
        </HeaderCount>
      </HeaderRow>

      {earned.length > 0 && (
        <ChipRow>
          {earned.map((badge) => (
            <Tooltip key={badge.key} title={badge.description}>
              <EarnedChip tabIndex={0}>
                <span aria-hidden>{iconFor(badge.key)}</span>
                {badge.label}
              </EarnedChip>
            </Tooltip>
          ))}
        </ChipRow>
      )}

      {closest && (
        <>
          {earned.length > 0 && <Divider />}
          <ClosestLabel>Closest to earning</ClosestLabel>
          <Tooltip title={closest.description}>
            <IconAndName tabIndex={0}>
              <span aria-hidden>{iconFor(closest.key)}</span>
              <BadgeName>{closest.label}</BadgeName>
            </IconAndName>
          </Tooltip>
          <ProgressBar
            variant='determinate'
            value={Math.min(
              100,
              (closest.current / (closest.threshold || 1)) * 100,
            )}
          />
          <ProgressCaption>
            {closest.current} / {closest.threshold}
            {closest.hint ? ` - ${closest.hint} to go` : ''}
          </ProgressCaption>
        </>
      )}

      {restInProgress.length > 0 && (
        <>
          {showAllInProgress && (
            <Box sx={{ mt: 1 }}>
              {restInProgress.map((badge) => (
                <Tooltip key={badge.key} title={badge.description}>
                  <CompactRow tabIndex={0}>
                    <span aria-hidden>{iconFor(badge.key)}</span>
                    <CompactName>{badge.label}</CompactName>
                    <CompactCount>
                      {badge.current}/{badge.threshold}
                    </CompactCount>
                  </CompactRow>
                </Tooltip>
              ))}
            </Box>
          )}
          <ToggleButton
            onClick={() => setShowAllInProgress((previous) => !previous)}
          >
            {showAllInProgress
              ? 'Show less'
              : `Show all ${restInProgress.length} in progress`}
          </ToggleButton>
        </>
      )}
    </RailCard>
  )
}

export default BadgesSection
